import React, { useState } from "react"
import { useSearchParams } from "react-router"
import { useTranslations } from "@/hooks/useTranslations"
import { Eye, EyeOff, Loader2, Lock, LogIn, ShieldCheck, AlertCircle } from "lucide-react"

import { Button } from "@/components/ui/button"
import { useAuth as useModuleAuth } from "@optimgov/module-auth"
import type { AuthError } from "@/lib/auth-actions"
import { LocalRoleSelector } from "@/components/LocalRoleSelector"

export default function LoginForm() {
	const t = useTranslations("login")
	const [searchParams] = useSearchParams()
	const returnUrl = searchParams.get("returnUrl")
	const { login, isAuthenticated } = useModuleAuth()
	// With module-auth, Azure login is always available (login delegates to MSAL)
	const azureEnabled = true
	const credentialsEnabled = false

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<AuthError | string | null>(null)

	// Credentials form state (kept for backward compat, credentials path disabled)
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [showPassword, setShowPassword] = useState(false)

	// Redirect if already authenticated
	React.useEffect(() => {
		if (isAuthenticated) {
			window.location.href = returnUrl || "/"
		}
	}, [isAuthenticated, returnUrl])

	const handleAzureLogin = async () => {
		setLoading(true)
		setError(null)

		try {
			await login()
		} catch (err) {
			setLoading(false)
			setError({
				code: "azure/login-failed",
				message: err instanceof Error ? err.message : "Unknown error",
				userFriendlyMessage: "Error en iniciar sessio amb Azure. Prova-ho de nou.",
			})
		}
	}

	const handleCredentialsLogin = async (e: React.FormEvent) => {
		e.preventDefault()
		// Credentials login is deprecated -- module-auth handles MSAL login
		setError("Credentials login is no longer supported. Use Microsoft sign-in.")
	}

	const errorMessage = typeof error === "string" ? error : error?.userFriendlyMessage

	return (
		<div className='w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden'>
			{/* Green Header */}
			<div className='bg-primary px-6 py-4'>
				<div className='flex items-center justify-center gap-2 text-white'>
					<Lock className='h-5 w-5' />
					<span className='font-medium'>{t("secureAccess")}</span>
				</div>
			</div>

			{/* Form Content */}
			<div className='p-6 sm:p-8'>
				<div className='text-center mb-6'>
					<h1 className='text-2xl font-bold text-gray-700 mb-2'>{t("title")}</h1>
					<p className='text-sm text-gray-400'>{t("subtitle")}</p>
				</div>

				{/* Error Message */}
				{errorMessage && (
					<div className='relative mb-5 bg-red-50 border border-red-200 text-red-700 pl-4 pr-9 py-3 rounded-lg text-sm flex items-start gap-2'>
						<AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0' />
						<p className='flex-1'>{errorMessage}</p>
						<button
							type='button'
							onClick={() => setError(null)}
							className='absolute top-2.5 right-2.5 flex items-center justify-center w-5 h-5 rounded hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors'
							aria-label='Dismiss'
						>
							<span className='text-base leading-none'>&times;</span>
						</button>
					</div>
				)}

				{/* Local mode: role selector */}
				{import.meta.env.VITE_LOCAL_MODE === "true" ? (
					<LocalRoleSelector />
				) : azureEnabled ? (
					<div className='space-y-5'>
						<Button
							type='button'
							onClick={handleAzureLogin}
							disabled={loading}
							size='lg'
							className='w-full py-3 text-base font-semibold bg-primary hover:bg-brand-forest-start text-white'
						>
							{loading ? (
								<>
									<Loader2 className='mr-2 h-5 w-5 animate-spin' />
									{t("connectingMicrosoft")}
								</>
							) : (
								<>
									<svg className='mr-2 h-5 w-5' viewBox='0 0 21 21' fill='none' xmlns='http://www.w3.org/2000/svg'>
										<rect x='1' y='1' width='9' height='9' fill='#F25022' />
										<rect x='11' y='1' width='9' height='9' fill='#7FBA00' />
										<rect x='1' y='11' width='9' height='9' fill='#00A4EF' />
										<rect x='11' y='11' width='9' height='9' fill='#FFB900' />
									</svg>
									{t("signInMicrosoft")}
								</>
							)}
						</Button>
					</div>
				) : null}

				{/* Credentials (email/password) login */}
				{credentialsEnabled && (
					<form onSubmit={handleCredentialsLogin} className='space-y-5'>
						{/* Email Field */}
						<div className='space-y-2'>
							<label htmlFor='email' className='block text-sm font-medium text-gray-500'>
								{t("email")}
							</label>
							<input
								id='email'
								type='email'
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className='block w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-gray-700 placeholder:text-gray-400'
								placeholder={t("emailPlaceholder")}
								autoComplete='email'
								disabled={loading}
							/>
						</div>

						{/* Password Field */}
						<div className='space-y-2'>
							<label htmlFor='password' className='block text-sm font-medium text-gray-500'>
								{t("password")}
							</label>
							<div className='relative'>
								<input
									id='password'
									type={showPassword ? "text" : "password"}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									className='block w-full px-4 py-3 pr-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-gray-700 placeholder:text-gray-400'
									placeholder={t("passwordPlaceholder")}
									autoComplete='current-password'
									disabled={loading}
								/>
								<button
									type='button'
									onClick={() => setShowPassword(!showPassword)}
									className='absolute inset-y-0 right-0 pr-4 flex items-center'
								>
									{showPassword ? (
										<EyeOff className='h-5 w-5 text-gray-400 hover:text-gray-600' />
									) : (
										<Eye className='h-5 w-5 text-gray-400 hover:text-gray-600' />
									)}
								</button>
							</div>
						</div>

						{/* Submit Button */}
						<Button
							type='submit'
							size='lg'
							className='w-full py-3 text-base font-semibold bg-primary hover:bg-brand-forest-start text-white'
							disabled={loading}
						>
							{loading ? (
								<>
									<Loader2 className='mr-2 h-5 w-5 animate-spin' />
									{t("signingIn")}
								</>
							) : (
								<>
									<LogIn className='mr-2 h-5 w-5' />
									{t("signIn")}
								</>
							)}
						</Button>
					</form>
				)}

				{/* No provider configured */}
				{!azureEnabled && !credentialsEnabled && (
					<div className='p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800'>
						{t("notConfigured")}
					</div>
				)}

				{/* Secure Connection Badge */}
				<div className='mt-6 flex items-center justify-center gap-2 text-sm text-gray-400'>
					<ShieldCheck className='h-4 w-4' />
					<span>{t("secureConnection")}</span>
				</div>
			</div>
		</div>
	)
}
